import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Truck } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import PartnerCredentialsModal from './PartnerCredentialsModal';

const TYPES = [
  { value: 'private',        label: 'Private' },
  { value: 'government',     label: 'Government (108)' },
  { value: 'hospital_owned', label: 'Hospital-owned' },
  { value: 'individual',     label: 'Individual Driver' },
];

const EMPTY = {
  name: '', provider_type: 'private', driver_name: '', vehicle_number: '',
  mobile: '', alt_mobile: '', city: '', base_location: '',
  default_incentive_amount: '0', is_verified: false, notes: '',
};

export default function NewAmbulanceProvider() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { create: apiCreate, update: apiUpdate, fetch: apiFetch } = useApiMethods({ throwOnFail: true });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [credsModal, setCredsModal] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await apiFetch(`engage/ambulance-providers/${id}/`);
        const d = res?.data || res?.data?.data;
        if (d) setForm({ ...EMPTY, ...d });
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load provider');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        await apiUpdate(`/engage/ambulance-providers/${id}/`, form);
        navigate(`/engage/ambulance-providers/${id}`);
      } else {
        const resp = await apiCreate('/engage/ambulance-providers/', form);
        const created = resp?.data || resp?.data?.data;
        const generatedCreds = resp?.generated_credentials || resp?.data?.generated_credentials;
        if (generatedCreds?.created && generatedCreds?.password) {
          setSaving(false);
          setCredsModal({ creds: generatedCreds, providerId: created?.id, name: created?.name || form.name });
          return;
        }
        navigate(created?.id ? `/engage/ambulance-providers/${created.id}` : '/engage/ambulance-providers');
      }
    } catch (err) {
      setError(err?.response?.data?.message || (err?.response?.data && JSON.stringify(err.response.data)) || 'Failed to save');
      setSaving(false);
    }
  };

  const backTo = isEdit ? `/engage/ambulance-providers/${id}` : '/engage/ambulance-providers';
  const INPUT = "w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500";
  const L = ({ children }) => <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{children}</label>;

  if (loading) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <button onClick={() => navigate(backTo)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white shadow-sm"><Truck className="w-5 h-5" /></div>
        <div><h1 className="text-xl font-bold text-gray-900 dark:text-white">{isEdit ? 'Edit' : 'Add'} Ambulance Partner</h1></div>
      </div>
      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}
      <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><L>Provider name <span className="text-red-500">*</span></L><input required value={form.name} onChange={e => set('name', e.target.value)} className={INPUT} placeholder="Kongu Ambulance Service" /></div>
          <div><L>Mobile <span className="text-red-500">*</span></L><input required value={form.mobile} onChange={e => set('mobile', e.target.value)} className={INPUT} placeholder="+91…" /></div>
          <div>
            <L>Provider type</L>
            <select value={form.provider_type} onChange={e => set('provider_type', e.target.value)} className={INPUT}>
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div><L>Vehicle number</L><input value={form.vehicle_number} onChange={e => set('vehicle_number', e.target.value)} className={INPUT} placeholder="TN-38-AB-1234" /></div>
          <div><L>Driver name</L><input value={form.driver_name} onChange={e => set('driver_name', e.target.value)} className={INPUT} /></div>
          <div><L>Alt mobile</L><input value={form.alt_mobile} onChange={e => set('alt_mobile', e.target.value)} className={INPUT} /></div>
          <div><L>City</L><input value={form.city} onChange={e => set('city', e.target.value)} className={INPUT} /></div>
          <div><L>Base location</L><input value={form.base_location} onChange={e => set('base_location', e.target.value)} className={INPUT} /></div>
          <div><L>Default incentive per referral (₹)</L><input type="number" value={form.default_incentive_amount} onChange={e => set('default_incentive_amount', e.target.value)} className={INPUT} /></div>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" id="ap-verified" checked={!!form.is_verified} onChange={e => set('is_verified', e.target.checked)} className="w-4 h-4" />
            <label htmlFor="ap-verified" className="text-sm text-gray-700 dark:text-gray-300">Verified provider</label>
          </div>
        </div>
        <div><L>Notes</L><textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} className={INPUT} /></div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button type="button" onClick={() => navigate(backTo)} className="px-4 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
          <button type="submit" disabled={saving || !form.name.trim() || !form.mobile.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm shadow-sm hover:bg-orange-700 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Provider')}
          </button>
        </div>
      </form>

      {credsModal && (
        <PartnerCredentialsModal
          partnerName={credsModal.name}
          partnerKind="ambulance"
          credentials={credsModal.creds}
          onClose={() => {
            const dest = credsModal.providerId ? `/engage/ambulance-providers/${credsModal.providerId}` : '/engage/ambulance-providers';
            setCredsModal(null);
            navigate(dest);
          }}
        />
      )}
    </div>
  );
}
